import { formatDistanceToNow } from 'date-fns';
import { AmountDisplay } from '@/components/common/AmountDisplay';
import { getActivityIconConfig } from '@/lib/activityFormatter';
import type { FormattedActivity } from '@/lib/activityFormatter';

interface ActivityItemProps {
  activity: FormattedActivity;
  onClick?: () => void;
}

/** Whether this activity type carries a monetary amount worth displaying. */
function hasAmount(a: FormattedActivity): boolean {
  return (
    a.amountMinor != null &&
    a.currency != null &&
    ['expense.created', 'expense.updated', 'expense.deleted', 'settlement.created'].includes(a.activityType)
  );
}

export function ActivityItem({ activity, onClick }: ActivityItemProps) {
  const { Icon, bgClass, iconClass } = getActivityIconConfig(activity.activityType);
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      onClick={onClick}
      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${onClick ? 'hover:bg-surface-hover cursor-pointer' : ''}`}
    >
      {/* Icon circle */}
      <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${bgClass}`}>
        <Icon className={`h-5 w-5 ${iconClass}`} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text-primary">{activity.title}</p>
        {activity.subtitle && (
          <p className="mt-0.5 truncate text-xs text-text-tertiary">{activity.subtitle}</p>
        )}
        <div className="mt-1 flex items-center gap-1.5 text-xs text-text-quaternary">
          {activity.groupName && (
            <>
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: activity.groupColor ?? '#6366F1' }}
              />
              <span className="truncate" style={{ color: activity.groupColor ?? '#6366F1' }}>
                {activity.groupName}
              </span>
              <span>&middot;</span>
            </>
          )}
          <span className="whitespace-nowrap">
            {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
          </span>
        </div>
      </div>

      {/* Amount */}
      {hasAmount(activity) && (
        <div className="shrink-0 pt-0.5">
          <AmountDisplay
            amountMinor={activity.amountMinor!}
            currency={activity.currency!}
            className="text-sm font-medium text-text-primary"
          />
        </div>
      )}
    </Tag>
  );
}
